import { useEffect, useRef, useState } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import ConfirmDialog from '../ConfirmDialog';
import { apiClient } from '../../utils/apiClient';
import {
  STATUS_OPTIONS, isDirty, toEditForm, toPayload, validateCustomerForm
} from '../../utils/customerView';

// Field row: label above, error below (announced), hint optional. Keeps every
// input labelled and tied to its error for assistive tech.
function Field({ id, label, error, hint, wide, required, children }) {
  return (
    <div className={`cx-field ${wide ? 'is-wide' : ''} ${error ? 'has-error' : ''}`}>
      <label htmlFor={id}>{label}{required && <span className="cx-req" aria-hidden="true"> *</span>}</label>
      {children}
      {hint && !error && <span className="cx-hint" id={`${id}-hint`}>{hint}</span>}
      {error && <span className="cx-error" id={`${id}-error`} role="alert">{error}</span>}
    </div>
  );
}

// Edit customer in a proper dialog: grouped sections, two columns on desktop,
// inline errors that say how to fix them (focus jumps to the first), a Save
// that only enables once something changed, and a "discard changes?" guard.
export default function CustomerEditDialog({ open, customer, userId, onClose, onSaved }) {
  const [form, setForm] = useState(() => toEditForm(customer));
  const [initial, setInitial] = useState(() => toEditForm(customer));
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState('');
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const formRef = useRef(null);

  // A fresh copy each time it opens.
  useEffect(() => {
    if (!open) return;
    const fresh = toEditForm(customer);
    setForm(fresh);
    setInitial(fresh);
    setErrors({});
    setServerError('');
    setConfirmDiscard(false);
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const dirty = isDirty(initial, form);
  const set = (field) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const requestClose = () => {
    if (saving) return;
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = validateCustomerForm(form);
    setErrors(found);
    if (Object.keys(found).length > 0) {
      const first = Object.keys(found)[0];
      const el = formRef.current && formRef.current.querySelector(`[name="${first}"]`);
      if (el) el.focus();
      return;
    }
    setSaving(true);
    setServerError('');
    try {
      const updated = await apiClient.put(`/api/users/${userId}`, toPayload(form));
      onSaved(updated);
    } catch (err) {
      setServerError(err.message || 'Could not save the changes. Check your connection and try again.');
    } finally {
      setSaving(false);
    }
  };

  const err = (field) => (errors[field] ? { 'aria-invalid': true, 'aria-describedby': `f-${field}-error` } : {});

  return (
    <>
      <Modal
        open={open}
        onClose={requestClose}
        title="Edit customer"
        size="large"
        footer={(
          <>
            <span className="cx-dialog-note" aria-live="polite">{dirty ? 'Unsaved changes' : ''}</span>
            <Button variant="secondary" onClick={requestClose} disabled={saving}>Cancel</Button>
            <Button type="submit" form="customer-edit-form" disabled={saving || !dirty}>{saving ? 'Saving…' : 'Save Changes'}</Button>
          </>
        )}
      >
        <form id="customer-edit-form" ref={formRef} onSubmit={submit} noValidate className="cx-form">
          {serverError && <p className="cx-banner cx-banner-error" role="alert">{serverError}</p>}

          <fieldset className="cx-section">
            <legend>Name</legend>
            <div className="cx-grid">
              <Field id="f-first_name" label="First name" required error={errors.first_name}>
                <input id="f-first_name" name="first_name" type="text" autoComplete="given-name" value={form.first_name} onChange={set('first_name')} {...err('first_name')} />
              </Field>
              <Field id="f-last_name" label="Last name" error={errors.last_name}>
                <input id="f-last_name" name="last_name" type="text" autoComplete="family-name" value={form.last_name} onChange={set('last_name')} {...err('last_name')} />
              </Field>
              <Field id="f-companyName" label="Company" wide hint="Shown as the customer's name across the app.">
                <input id="f-companyName" name="companyName" type="text" autoComplete="organization" value={form.companyName} onChange={set('companyName')} />
              </Field>
            </div>
          </fieldset>

          <fieldset className="cx-section">
            <legend>Contact</legend>
            <div className="cx-grid">
              <Field id="f-email" label="Email" required error={errors.email}>
                <input id="f-email" name="email" type="email" inputMode="email" autoComplete="email" spellCheck={false} placeholder="name@example.com" value={form.email} onChange={set('email')} {...err('email')} />
              </Field>
              <Field id="f-phoneNumber" label="Phone" error={errors.phoneNumber}>
                <input id="f-phoneNumber" name="phoneNumber" type="tel" inputMode="tel" autoComplete="tel" placeholder="+44 20 7946 0000" value={form.phoneNumber} onChange={set('phoneNumber')} {...err('phoneNumber')} />
              </Field>
            </div>
          </fieldset>

          <fieldset className="cx-section">
            <legend>Address</legend>
            <div className="cx-grid">
              <Field id="f-address1" label="Address line 1" wide>
                <input id="f-address1" name="address1" type="text" autoComplete="address-line1" value={form.address1} onChange={set('address1')} />
              </Field>
              <Field id="f-address2" label="Address line 2" wide>
                <input id="f-address2" name="address2" type="text" autoComplete="address-line2" value={form.address2} onChange={set('address2')} />
              </Field>
              <Field id="f-city" label="City">
                <input id="f-city" name="city" type="text" autoComplete="address-level2" value={form.city} onChange={set('city')} />
              </Field>
              <Field id="f-county" label="County">
                <input id="f-county" name="county" type="text" autoComplete="address-level1" value={form.county} onChange={set('county')} />
              </Field>
              <Field id="f-postcode" label="Postcode">
                <input id="f-postcode" name="postcode" type="text" autoComplete="postal-code" spellCheck={false} value={form.postcode} onChange={set('postcode')} />
              </Field>
              <Field id="f-country" label="Country">
                <input id="f-country" name="country" type="text" autoComplete="country-name" value={form.country} onChange={set('country')} />
              </Field>
            </div>
          </fieldset>

          <fieldset className="cx-section">
            <legend>Account</legend>
            <div className="cx-segmented" role="radiogroup" aria-label="Account status">
              {STATUS_OPTIONS.map((status) => (
                <label key={status} className={`cx-seg ${form.status === status ? 'is-on' : ''}`}>
                  <input type="radio" name="status" value={status} checked={form.status === status} onChange={set('status')} />
                  {status}
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="cx-section">
            <legend>Staff note</legend>
            <Field id="f-notes" label="Private note" wide hint="Only staff can see this.">
              <textarea id="f-notes" name="notes" rows={4} placeholder="Anything the team should know about this customer…" value={form.notes} onChange={set('notes')} />
            </Field>
          </fieldset>
        </form>
      </Modal>

      <ConfirmDialog
        open={confirmDiscard}
        title="Discard your changes?"
        message="You have unsaved changes to this customer. If you close now they will be lost."
        confirmLabel="Discard changes"
        cancelLabel="Keep editing"
        onConfirm={() => { setConfirmDiscard(false); onClose(); }}
        onCancel={() => setConfirmDiscard(false)}
      />
    </>
  );
}
